/* ═══ THE INSIDE TOP — the customer's own words or photo above the pop-up (2026-10-08) ═══
 *
 * Decision (venture repo, decisions.md, 8 Oct): a card's occasion comes from its COVER. On a design
 * whose cover is generic, the occasion greeting printed inside was stripped (`top-plain`), and the
 * customer chooses what goes there: a title and subtitle from a template ("like Canva", Daniel: the
 * same placeholder words, different fonts and colours, and they can change the font and the colour),
 * or a photo. Anything on the inside top may print full bleed (Daniel, 8 Oct); the WORDS stay inside
 * the 8 mm safe margin.
 *
 * ⭐ ONE RENDERER, TWO GRIDS. Everything is laid out in millimetres on the card's trim (92 x 120, y
 * down from the top edge, the fold at 120) and drawn through a geometry:
 *   - PREVIEW: the web face the 3D card shows (CoverTop), the trim stretched to 1000 x 1332;
 *   - MASTER:  the print grid, 98 x 123 mm with 3 mm bleed on the three outer edges, 2315 x 2906.
 * So what the customer sees is what prints: the layer the builder bakes is the same drawing on the
 * print grid, sent as `_Photo 5` and drawn by print over the design's text-free top (print/photos.mjs
 * attachTopLayer). The words are pixels by the time they reach print, so any face here is fine.
 *
 * ⛔ THE GLUE BAND TAKES NO INK: trim x 10-82 from y 80 down is cleared in the preview and in the
 * text bake (print clears it again, 0.5 mm wider). The photo bake is a JPEG and is NOT cleared here:
 * a cleared JPEG pixel would be black (canvas, 2026-09); print clears it. A photo baked as a PNG (small, see-through
 * or cut out, 24 Oct) is cleared here like the words.
 *
 * No Alpine in here: configurator.js owns the state and calls window.PPTop.
 */
(function () {
  'use strict';

  /* The faces. `scale` evens out how big a face looks at the same size (Amatic is tall and narrow,
     Erica wide and heavy), so a font swap keeps the title about the same weight on the panel.
     Self-hosted, OFL, from Yonatan's picturepops-print/src/fonts; declared in pp-top-fonts.liquid. */
  var FONTS = {
    'dm-serif':  { label: 'Classic',    family: 'PPTop DM Serif',  weight: 400, style: 'normal', scale: 1 },
    'playfair':  { label: 'Elegant',    family: 'PPTop Playfair',  weight: 600, style: 'italic', scale: 1.02 },
    'caslon':    { label: 'Caslon',     family: 'PPTop Caslon',    weight: 400, style: 'normal', scale: 1.06 },
    'fell':      { label: 'Storybook',  family: 'PPTop Fell',      weight: 400, style: 'normal', scale: 1.08 },
    'gloock':    { label: 'Modern',     family: 'PPTop Gloock',    weight: 400, style: 'normal', scale: 0.98 },
    'alfa':      { label: 'Poster',     family: 'PPTop Alfa',      weight: 400, style: 'normal', scale: 0.86 },
    'amatic':    { label: 'Chalk',      family: 'PPTop Amatic',    weight: 700, style: 'normal', scale: 1.42 },
    'erica':     { label: 'Bold',       family: 'PPTop Erica',     weight: 400, style: 'normal', scale: 0.84 },
    'nothing':   { label: 'Handwritten', family: 'PPTop Nothing',  weight: 400, style: 'normal', scale: 1.02 },
    'outfit':    { label: 'Clean',      family: 'PPTop Outfit',    weight: 700, style: 'normal', scale: 0.92 },
    // Subtitle faces (not offered as titles)
    'outfit-sub':  { family: 'PPTop Outfit',   weight: 400, style: 'normal', scale: 1 },
    'poppins-sub': { family: 'PPTop Poppins',  weight: 500, style: 'normal', scale: 1 },
    'oldstd-sub':  { family: 'PPTop OldStd',   weight: 400, style: 'italic', scale: 1.08 },
    'fell-sub':    { family: 'PPTop Fell',     weight: 400, style: 'italic', scale: 1.1 },
    'amatic-sub':  { family: 'PPTop Amatic',   weight: 700, style: 'normal', scale: 1.4 },
    'playfair-sub':{ family: 'PPTop Playfair', weight: 400, style: 'italic', scale: 1.02 }
  };
  var TITLE_FONTS = ['dm-serif', 'playfair', 'caslon', 'fell', 'gloock', 'alfa', 'amatic', 'erica', 'nothing', 'outfit'];

  // The accent colours on offer. Deep enough to read on white paper at arm's length.
  var COLOURS = [
    { key: 'ink',    hex: '#1B2433', label: 'Ink' },
    { key: 'navy',   hex: '#1F4FA3', label: 'Blue' },
    { key: 'red',    hex: '#C8372D', label: 'Red' },
    { key: 'coral',  hex: '#E0664B', label: 'Coral' },
    { key: 'rose',   hex: '#C2577A', label: 'Rose' },
    { key: 'plum',   hex: '#6B3A6E', label: 'Plum' },
    { key: 'forest', hex: '#2F6B4F', label: 'Green' },
    { key: 'olive',  hex: '#7D6B3D', label: 'Olive' },
    { key: 'gold',   hex: '#B8892B', label: 'Gold' },
    { key: 'teal',   hex: '#1F7A80', label: 'Teal' }
  ];

  /* The templates. Sizes are cap-to-descender em sizes in mm, the LARGEST the words may be; the fitter
     only ever shrinks (and the floor keeps them legible: TITLE_MIN / SUB_MIN). `accent` is the
     template's own colour until the customer picks one. `sub.color: 'accent'` follows it too.
       bg     'none' | 'fill' (the accent behind white words) | 'tint' (a pale wash of the accent)
       deco   'none' | 'rule' (a short line between the lines) | 'frame' (a double rule around them)
              | 'dots' (three dots above the title) */
  var TEMPLATES = [
    { key: 'classic',  label: 'Classic',  accent: 'ink',
      title: { font: 'dm-serif', size: 11.5 },
      sub:   { font: 'outfit-sub', size: 3.1, upper: true, track: 0.2, color: '#47526A' }, gap: 3.2, bg: 'none', deco: 'none' },
    { key: 'garden',   label: 'Garden',   accent: 'forest',
      title: { font: 'playfair', size: 11.5 },
      sub:   { font: 'oldstd-sub', size: 4.6, color: '#47526A' }, gap: 2.6, bg: 'none', deco: 'rule' },
    { key: 'storybook', label: 'Storybook', accent: 'red',
      title: { font: 'fell', size: 11.5 },
      sub:   { font: 'fell-sub', size: 4.8, color: 'accent' }, gap: 2.4, bg: 'none', deco: 'dots' },
    { key: 'poster',   label: 'Poster',   accent: 'coral',
      title: { font: 'alfa', size: 11, upper: true, track: 0.02 },
      sub:   { font: 'poppins-sub', size: 3.3, upper: true, track: 0.16, color: '#1B2433' }, gap: 3, bg: 'none', deco: 'none' },
    { key: 'handwritten', label: 'Handwritten', accent: 'navy',
      title: { font: 'nothing', size: 11 },
      sub:   { font: 'outfit-sub', size: 3.6, color: '#47526A' }, gap: 3.4, bg: 'none', deco: 'none' },
    { key: 'chalk',    label: 'Festive',  accent: 'red',
      title: { font: 'amatic', size: 15, upper: true, track: 0.04 },
      sub:   { font: 'amatic-sub', size: 6.4, upper: true, track: 0.06, color: '#1B2433' }, gap: 1.6, bg: 'none', deco: 'none' },
    { key: 'framed',   label: 'Framed',   accent: 'gold',
      title: { font: 'caslon', size: 11, color: '#1B2433' },
      sub:   { font: 'oldstd-sub', size: 4.2, color: '#47526A' }, gap: 2.4, bg: 'none', deco: 'frame' },
    { key: 'modern',   label: 'Modern',   accent: 'plum',
      title: { font: 'gloock', size: 12 },
      sub:   { font: 'poppins-sub', size: 3.4, color: '#47526A' }, gap: 2.8, bg: 'none', deco: 'rule' },
    { key: 'block',    label: 'Colour block', accent: 'navy',
      title: { font: 'erica', size: 10.5, upper: true, color: '#FFFFFF' },
      sub:   { font: 'outfit-sub', size: 3.2, upper: true, track: 0.2, color: '#FFFFFF' }, gap: 3, bg: 'fill', deco: 'none' },
    { key: 'blush',    label: 'Soft wash', accent: 'rose',
      title: { font: 'playfair', size: 11.5 },
      sub:   { font: 'playfair-sub', size: 4.4, color: '#47526A' }, gap: 2.4, bg: 'tint', deco: 'none' }
  ];
  /* ⭐ THE WORDS A FRESH INSIDE TOP STARTS WITH, AND ITS TEMPLATE: ONE COPY, HERE (2026-10-08). The builder
     (configurator.js topOnDesign) and a design's lightbox (rd-design.js, the 3D Inside) both start from
     these, so the words a visitor sees inside the card on a design's page are the words the builder
     then opens with. Until 8 Oct they lived in configurator.js (TOP_SUBTITLE, TOP_TITLE and the fallback).

     Daniel, 8 Oct: "All that matters is which occasion it's picked for." The title is the occasion's
     own headline (Shopify's `occasion` metaobject, window.PP_OCCASIONS, passed in as `headline`), except
     where TITLE says otherwise; the subtitle is SUB's line. Warm and plain (voice.md); every template
     shows the same words. "I trust you with the wording."

     ⛔ NO OCCASION IS ITS OWN PAIR, NOT JUST BECAUSE'S. A card picked from All cards, a search, a series
     or an artist's page carries no occasion, and starts on NONE (Daniel, 8 Oct: "For a good friend" /
     "So glad you are in my life", no full stop, like the others). Just because is an occasion a visitor
     picks, and keeps its own words. The ORDER may still say Just because for a generic card (routing,
     rd-design.js occasionFor); the words do not follow that. `any` (the builder's ?for=any) has no
     headline and no line here, so it reads as none. */
  /* THE FIVE LAYOUTS OFFERED (Daniel, 19 Oct, after the sync with Yonatan: "keep about five"; his pick of Claude's
     suggestion). The other five stay in TEMPLATES, so a draft or an order that has one still draws and prints it. */
  var OFFERED = ['classic', 'handwritten', 'chalk', 'block', 'blush'];
  var START = 'classic';                         // the template a fresh inside top starts on (configurator.js emptyTop)
  var SUB = {
    'birthday': 'Wishing you the happiest day',
    'valentines': 'You have my whole heart',
    'christmas': 'With love this season',
    'rosh-hashanah': 'Wishing you a sweet new year',
    'halloween': 'Wishing you a night of treats',
    'just-because': 'Thinking of you today',
    /* The rest of the 41-occasion guide (2026-10-08), so a stripped design bought for any of them starts on words
       that fit it, not on its original greeting. */
    'anniversary': 'To many more years together',
    'wedding': 'Wishing you a lifetime of happiness',
    'engagement': 'Wishing you both every happiness',
    'new-baby': 'Wishing your family so much joy',
    'pregnancy-cards': 'Wishing you all the joy to come',
    'graduation': 'So proud of you',
    'new-job': 'Wishing you a great start',
    'retirement': 'Enjoy every day ahead',
    'new-home': 'Wishing you happy days in your new place',
    'congratulations': 'Well done, and well deserved',
    'thank-you': 'For everything you do',
    'love': 'You mean the world to me',
    'friendship': 'So glad you are in my life',
    'thinking-of-you': 'Sending you love today',
    'miss-you': 'Wishing you were here',
    'good-luck': 'Wishing you all the best',
    'bon-voyage': 'Safe travels, and come back soon',
    'apology': 'I hope you can forgive me',
    'sympathy': 'Holding you close',
    'pet-sympathy': 'Remembering a dear friend',
    'get-well': 'Wishing you well, every day',
    'encouragement': 'I believe in you',
    'diwali': 'Wishing you light and joy',
    'thanksgiving': 'Grateful for you',
    'hanukkah': 'Wishing you eight bright nights',
    'kwanzaa': 'Wishing you and yours a joyful year',
    'new-year': 'Wishing you a wonderful year ahead',
    'lunar-new-year': 'Wishing you good fortune',
    'st-patricks-day': 'A little luck your way',
    'easter': 'Wishing you a joyful spring',
    'passover': 'With love from our table to yours',
    'mothers-day': 'With all my love',
    'fathers-day': 'Thanks for everything, Dad',
    'fourth-of-july': 'Wishing you a happy summer',
    'grandparents-day': 'For all the love you give'
  };
  /* The title is the occasion's own headline, except where that headline is not something to print as a greeting
     inside a card: "With love" for a sympathy card, "For you both" for grandparents who may be one. */
  var TITLE = {
    'sympathy': 'With deepest sympathy',
    'pet-sympathy': 'With deepest sympathy',
    'grandparents-day': 'Happy Grandparents Day'
  };
  var NONE = { title: 'For a good friend', subtitle: 'So glad you are in my life' };
  function words(occ, headline) {
    return { title: (occ && (TITLE[occ] || headline)) || NONE.title, subtitle: (occ && SUB[occ]) || NONE.subtitle };
  }

  var TITLE_MIN = 3.6, SUB_MIN = 2.2;            // mm: the floor the fitter shrinks to
  var SAFE = 8;                                  // mm, the safe margin the words keep (Daniel, 8 Oct)
  var BAND = { x0: 10, x1: 82, y0: 80 };         // the glue band, trim mm (print/geometry.mjs MM.inside.dead)
  var BLANK_BOX = [10, 60];                      // where the words go on a blank panel (trim mm, top..bottom)
  var ONE_LINE_BOX = 20;                         // mm: a box shorter than this takes a title and no subtitle (the pun cards: one lettered line, as their originals had)
  var K_FLOOR = 0.2;                             // below the legibility floor, as far as the fitter will go

  function oneLine(box) { return box[1] - box[0] < ONE_LINE_BOX; }
  /* The drawing's options for a design's words band: a blank panel may take a template's background all the
     way down; on a design with art the background stays in the band behind the words, so the art keeps
     showing (drawWords). The builder (configurator.js topOpts) and the lightbox both ask this. */
  function optsFor(box, photo) {
    box = box || BLANK_BOX;
    return { box: box, blank: box[1] - box[0] >= 40, photo: photo || null };
  }
  /* The inside top exactly as the builder starts it for `occ` on a design whose words band is `box`: the
     template START, no font or colour of the customer's, the occasion's words, and no subtitle in a band too
     short for one (configurator.js emptyTop, topOnDesign, topSpec). */
  function startSpec(occ, headline, box) {
    var w = words(occ, headline);
    return { mode: 'text', template: START, title: w.title, subtitle: oneLine(box || BLANK_BOX) ? '' : w.subtitle,
             font: null, color: null, photo: { zoom: 1, px: 0, py: 0 } };
  }
  function byKey(list, k) { for (var i = 0; i < list.length; i++) if (list[i].key === k) return list[i]; return null; }
  function hexOf(k) { var c = byKey(COLOURS, k); return c ? c.hex : (/^#[0-9a-f]{6}$/i.test(k || '') ? k : '#1B2433'); }
  function tint(hex, t) {                        // mix with white: t = share of the colour kept
    var n = parseInt(hex.slice(1), 16), r = n >> 16, g = (n >> 8) & 255, b = n & 255;
    var m = function (v) { return Math.round(255 - (255 - v) * t); };
    return 'rgb(' + m(r) + ',' + m(g) + ',' + m(b) + ')';
  }

  /* Geometries: trim mm -> canvas px. `full` is the face's extent in trim mm (with bleed on the
     master; the trim itself on the web face). */
  function previewGeom(w) {
    var h = Math.round(w * 1332 / 1000);
    return { w: w, h: h, sx: w / 92, sy: h / 120, ox: 0, oy: 0, full: { x0: 0, y0: 0, x1: 92, y1: 120 } };
  }
  var MASTER = { w: 2315, h: 2906, sx: 2315 / 98, sy: 2906 / 123, ox: 3 * 2315 / 98, oy: 3 * 2906 / 123,
                 full: { x0: -3, y0: -3, x1: 95, y1: 120 } };

  /* ⭐ THE LAYOUT'S COLOUR COMES FROM THE CARD (Daniel, 19 Oct: "how do you choose the colors of the layout? ... I think
     you should match them to the colors of the card"). Until then every layout had its own fixed accent whatever the
     cover. coverAccent reads the cover (CoverFront, 72 px on its long side), keeps the pixels with colour in them
     (saturation 0.25+, chroma 0.15+, lightness 0.12-0.85), buckets them by hue in 24 steps and takes the strongest
     bucket (see accentOfPixels). That colour is then darkened, keeping its hue, until it reads on the paper the words are
     printed on (contrast 4.5:1 against #F8F6F1, WCAG AA for text). A cover with no colour in it (an etching, black
     and white) gives null and the layout keeps its own accent. A colour the customer picks still wins (spec.color).
     The words reach print as pixels (`_Photo 5`), so any colour prints as shown. */
  var ACC = {};
  function lum(r, g, b) {
    var f = function (v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
  }
  var PAPER_L = lum(0xF8, 0xF6, 0xF1);
  function hex2(v) { return ('0' + Math.max(0, Math.min(255, Math.round(v))).toString(16)).slice(-2); }
  /* Each coloured pixel votes for its hue with its chroma squared, so the cover's vivid colours win over the large
     muted grounds they sit on (the Alhambra's blue and orange over its cream strapwork; measured 19 Oct: by plain
     area it chose a khaki). The bucket's colour is the vote-weighted mean of its pixels. */
  function accentOfPixels(d) {
    var B = 24, w = new Array(B).fill(0), sr = new Array(B).fill(0), sg = new Array(B).fill(0), sb = new Array(B).fill(0), px = 0;
    for (var i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 128) continue;
      var r = d[i] / 255, g = d[i + 1] / 255, b = d[i + 2] / 255;
      var mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2, c = mx - mn;
      if (!c) continue;
      var sat = c / (1 - Math.abs(2 * l - 1));
      if (sat < 0.25 || c < 0.15 || l < 0.12 || l > 0.85) continue;
      var h = mx === r ? ((g - b) / c) % 6 : (mx === g ? (b - r) / c + 2 : (r - g) / c + 4);
      var k = Math.floor(((h * 60 + 360) % 360) / (360 / B)), v = c * c;
      w[k] += v; sr[k] += d[i] * v; sg[k] += d[i + 1] * v; sb[k] += d[i + 2] * v; px++;
    }
    var bi = 0;
    for (var j = 1; j < B; j++) if (w[j] > w[bi]) bi = j;
    if (px < 12 || !w[bi]) return null;                       // no colour worth the name
    var R = sr[bi] / w[bi], G = sg[bi] / w[bi], Bl = sb[bi] / w[bi];
    for (var step = 0; step < 40; step++) {                    // darker, same hue, until it reads on the paper
      if ((PAPER_L + 0.05) / (lum(R, G, Bl) + 0.05) >= 4.5) break;
      R *= 0.93; G *= 0.93; Bl *= 0.93;
    }
    return '#' + hex2(R) + hex2(G) + hex2(Bl);
  }
  function coverAccent(src) {
    if (!src) return Promise.resolve(null);
    if (ACC[src]) return ACC[src];
    ACC[src] = new Promise(function (res) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = function () {
        try {
          var k = 72 / Math.max(im.naturalWidth, im.naturalHeight), w = Math.max(1, Math.round(im.naturalWidth * k)), h = Math.max(1, Math.round(im.naturalHeight * k));
          var cv = document.createElement('canvas'); cv.width = w; cv.height = h;
          var cx = cv.getContext('2d'); cx.drawImage(im, 0, 0, w, h);
          res(accentOfPixels(cx.getImageData(0, 0, w, h).data));
        } catch (e) { res(null); }                             // a tainted canvas: the layout's own accent
      };
      im.onerror = function () { res(null); };
      im.src = src;
    });
    return ACC[src];
  }

  // The resolved look: the template with the customer's font and colour applied (else the cover's, else its own).
  function look(spec) {
    var t = byKey(TEMPLATES, spec.template) || TEMPLATES[0];
    var accent = hexOf(spec.color || spec.auto || t.accent);
    var tf = FONTS[spec.font] && TITLE_FONTS.indexOf(spec.font) >= 0 ? spec.font : t.title.font;
    var title = Object.assign({}, t.title, { font: tf, size: t.title.size * FONTS[tf].scale / FONTS[t.title.font].scale });
    return { t: t, accent: accent, title: title, sub: t.sub,
             titleColor: t.title.color || accent,
             subColor: t.sub.color === 'accent' ? accent : (t.sub.color || '#47526A') };
  }

  function fontCss(f, px) { return f.style + ' ' + f.weight + ' ' + px + 'px "' + f.family + '"'; }

  /* Width of a line in mm at size `mm`, with tracking (em) done by hand: canvas letterSpacing is
     not in every engine this has to run in. This is the ADVANCE, which places the line. */
  function lineWidth(ctx, f, text, mm, track) {
    var REF = 100;
    ctx.font = fontCss(f, REF);
    if (!track) return ctx.measureText(text).width / REF * mm;
    /* A tracked line is drawn one letter at a time (drawLine), so it has no kerning: measured as
       the same letters, or it comes out wider than measured and over the margin (it did: Poster and
       Festive ran to 86 mm on the first sweep, 8 Oct). */
    var chars = Array.from(text), w = 0;
    for (var i = 0; i < chars.length; i++) w += ctx.measureText(chars[i]).width;
    return (w + track * REF * Math.max(0, chars.length - 1)) / REF * mm;
  }
  /* ⛔ THE WIDTH THE FITTER TESTS IS THE INK'S, NOT THE ADVANCE. An italic swash hangs outside its
     advance: Garden's Playfair italic put ink at 7.5 mm, inside the 8 mm safe margin the words may not
     enter (measured on the first end-to-end run, 8 Oct). The line is centred on its advance, so it
     needs the advance plus twice the larger overhang. */
  function inkWidth(ctx, f, text, mm, track) {
    var REF = 100;
    ctx.font = fontCss(f, REF);
    var m = ctx.measureText(text), adv = m.width;
    var ol = Math.max(0, m.actualBoundingBoxLeft || 0), or = Math.max(0, (m.actualBoundingBoxRight || adv) - adv);
    return lineWidth(ctx, f, text, mm, track) + 2 * Math.max(ol, or) / REF * mm;
  }
  function wrap(ctx, f, text, mm, track, width) {
    var words = String(text).split(/\s+/).filter(Boolean), lines = [], cur = '';
    for (var i = 0; i < words.length; i++) {
      var next = cur ? cur + ' ' + words[i] : words[i];
      if (!cur || inkWidth(ctx, f, next, mm, track) <= width) cur = next;
      else { lines.push(cur); cur = words[i]; }
    }
    if (cur) lines.push(cur);
    return lines;
  }
  function inkV(ctx, f, text) {                    // a line's real ink above and below its baseline, per em
    ctx.font = fontCss(f, 100);
    var m = ctx.measureText(text);
    return { a: (m.actualBoundingBoxAscent || 75) / 100, d: (m.actualBoundingBoxDescent || 20) / 100 };
  }
  function metrics(ctx, f) {                       // ascent and descent as a share of the em
    ctx.font = fontCss(f, 100);
    var m = ctx.measureText('HÁgjy');
    var a = m.fontBoundingBoxAscent, d = m.fontBoundingBoxDescent;
    if (!(a > 0)) { a = m.actualBoundingBoxAscent || 80; d = m.actualBoundingBoxDescent || 20; }
    return { a: a / 100, d: d / 100 };
  }

  /* ⭐ THE FIT: the largest scale k <= 1 at which every line fits the width, no part has more than two
     lines, and the block fits the box's height. Bisected, shrink only; at the floor it reports
     `overflow` rather than cutting a word (the note's rule: the fitter shrinks and never truncates). */
  function layout(ctx, spec, box) {
    var L = look(spec);
    var title = (spec.title || '').trim(), sub = oneLine(box) ? '' : (spec.subtitle || '').trim();
    var tf = FONTS[L.title.font], sf = FONTS[L.sub.font];
    // One line in a short box: no frame and no dots, which at that height are clutter round a word.
    var frame0 = L.t.deco === 'frame' && !oneLine(box) ? 4.2 : 0, dots = L.t.deco === 'dots' && !oneLine(box) ? 3.2 : 0;
    var tm = metrics(ctx, tf), sm = metrics(ctx, sf);
    var tUp = L.title.upper ? title.toUpperCase() : title, sUp = L.sub.upper ? sub.toUpperCase() : sub;
    function at(k, maxT) {
      // 0.2 mm of slack on every side: the fitter fills the box exactly, and the edge pixel of an
      // antialiased glyph would otherwise sit on the margin's line.
      var frame = frame0 * Math.min(1, k * 1.4), W = 92 - 2 * SAFE - 2 * frame - 0.4, H = box[1] - box[0] - 2 * frame - 0.4;
      var ts = L.title.size * k, ss = L.sub.size * k;
      var tl = tUp ? wrap(ctx, tf, tUp, ts, L.title.track || 0, W) : [];
      var sl = sUp ? wrap(ctx, sf, sUp, ss, L.sub.track || 0, W) : [];
      var tlh = ts * (tm.a + tm.d) * 0.92, slh = ss * (sm.a + sm.d) * 0.98;
      var rule = L.t.deco === 'rule' && tl.length && sl.length ? 2.6 * k : 0;
      /* ⛔ THE BOX HOLDS THE INK, NOT THE LINE BOXES. Each line's real ascent and descent (canvas
         actualBoundingBox), walked exactly the way drawWords walks the lines: Gloock's and Nothing You
         Could Do's descenders hung 0.1-0.2 mm below line boxes that fitted (sweep, 8 Oct). The block
         is centred on its ink too. Positions are from the block's top. */
      var y = tl.length ? dots * k : 0, inkTop = tl.length && dots ? 0.35 * k : Infinity, inkBot = -Infinity;
      tl.forEach(function (s) { var b = y + ts * tm.a * 0.92, v = inkV(ctx, tf, s); inkTop = Math.min(inkTop, b - v.a * ts); inkBot = Math.max(inkBot, b + v.d * ts); y += tlh; });
      if (tl.length && sl.length) { y += L.t.gap * k / (rule ? 2 : 1); if (rule) y += rule + L.t.gap * k / 2; }
      sl.forEach(function (s) { var b = y + ss * sm.a * 0.98, v = inkV(ctx, sf, s); inkTop = Math.min(inkTop, b - v.a * ss); inkBot = Math.max(inkBot, b + v.d * ss); y += slh; });
      var h = isFinite(inkTop) ? inkBot - inkTop : 0;
      var ok = tl.length <= maxT && sl.length <= 2 && h <= H &&
        tl.every(function (s) { return inkWidth(ctx, tf, s, ts, L.title.track || 0) <= W + 0.01; }) &&
        sl.every(function (s) { return inkWidth(ctx, sf, s, ss, L.sub.track || 0) <= W + 0.01; });
      return { ok: ok, k: k, ts: ts, ss: ss, tl: tl, sl: sl, tlh: tlh, slh: slh, rule: rule, h: h, frame: frame, H: H,
               inkTop: isFinite(inkTop) ? inkTop : 0 };
    }
    // The floor counts only the parts that are there: no subtitle, no subtitle minimum.
    var kMin = Math.min(1, Math.max(tUp ? TITLE_MIN / L.title.size : 0, sUp ? SUB_MIN / L.sub.size : 0));
    function search(maxT, floor) {
      var top = at(1, maxT);
      if (top.ok) return top;
      var lo = floor, hi = 1, found = null;
      for (var i = 0; i < 14; i++) { var mid = (lo + hi) / 2, r = at(mid, maxT); if (r.ok) { found = r; lo = mid; } else hi = mid; }
      return found;
    }
    /* ONE TITLE LINE WHEN IT COSTS LITTLE. "Happy birthday" set on two lines at full size reads as a
       poster; on one line a little smaller it reads as a card. Two lines win only when one would be
       more than a fifth smaller, or does not fit at all. */
    var one = search(1, kMin), two = search(2, kMin);
    var best = one && (!two || one.k >= 0.8 * two.k) ? one : two;
    /* ⛔ TOO LONG FOR THE FLOOR: SMALLER, NEVER OUTSIDE THE BOX. The words may not enter the safe
       margin (Daniel, 8 Oct) and on a design with art the box ends above it, so the fitter goes on
       shrinking and says so (`small`); the editor refuses typing past what fits, so this is reached
       only by a font or template with more width than the words were written for. */
    if (!best) { best = search(2, K_FLOOR); if (best) best.small = true; }
    if (!best) { best = at(K_FLOOR, 2); best.overflow = true; best.small = true; }
    best.L = L; best.box = box; best.dots = dots; best.tm = tm; best.sm = sm; best.kMin = kMin;
    // The block's top, placed so its INK is centred in the box (inside the frame's padding).
    best.top = box[0] + best.frame + 0.2 + (best.H - best.h) / 2 - best.inkTop;   // + half the 0.4 mm slack
    return best;
  }

  function drawLine(ctx, g, f, text, mm, track, cx, baseline, color) {
    var px = mm * g.sy;
    // Measured BEFORE the font is set: lineWidth sets its own reference size on the context.
    var wpx = track ? lineWidth(ctx, f, text, mm, track) * g.sy : 0;
    /* A tracked line's letters are placed by the SAME reference measurements the fitter used, scaled,
       never re-measured at the drawing size: Firefox measures small sizes a little wider, and over a
       tracked line that crossed the margin by 0.2 mm (Playwright sweep, 8 Oct). */
    var adv = [];
    if (track) {
      ctx.font = fontCss(f, 100);
      Array.from(text).forEach(function (ch) { adv.push(ctx.measureText(ch).width / 100 * px); });
    }
    ctx.save();
    ctx.translate(g.ox + cx * g.sx, g.oy + baseline * g.sy);
    ctx.scale(g.sx / g.sy, 1);                       // the web face is stretched 2%: so is everything on it
    ctx.font = fontCss(f, px);
    ctx.fillStyle = color;
    ctx.textBaseline = 'alphabetic';
    if (!track) { ctx.textAlign = 'center'; ctx.fillText(text, 0, 0); }
    else {
      ctx.textAlign = 'left';
      var chars = Array.from(text), x = -wpx / 2;
      for (var i = 0; i < chars.length; i++) { ctx.fillText(chars[i], x, 0); x += adv[i] + track * px; }
    }
    ctx.restore();
  }
  function rect(ctx, g, x0, y0, x1, y1) { ctx.fillRect(g.ox + x0 * g.sx, g.oy + y0 * g.sy, (x1 - x0) * g.sx, (y1 - y0) * g.sy); }

  /* Draw the words layer on `ctx` through geometry `g`. `opts.box` is the design's words band (trim
     mm, from the catalogue's topBox); `opts.blank` says the panel has no art, so a background may
     cover all of it rather than only the band behind the words. Returns the layout (for `overflow`). */
  function drawWords(ctx, g, spec, opts) {
    var box = (opts && opts.box) || BLANK_BOX, blank = !opts || opts.blank !== false;
    var r = layout(ctx, spec, box), L = r.L;
    if (L.t.bg !== 'none') {
      ctx.fillStyle = L.t.bg === 'fill' ? L.accent : tint(L.accent, 0.16);
      rect(ctx, g, g.full.x0, g.full.y0, g.full.x1, blank ? g.full.y1 : Math.min(box[1] + 3, 78));
    }
    var y = r.top;
    if (r.dots && r.tl.length) {
      ctx.fillStyle = L.accent;
      for (var d = -1; d <= 1; d++) {
        ctx.beginPath();
        ctx.arc(g.ox + (46 + d * 2.4 * r.k) * g.sx, g.oy + (y + 0.9 * r.k) * g.sy, 0.55 * r.k * g.sy, 0, 2 * Math.PI);
        ctx.fill();
      }
      y += r.dots * r.k;
    }
    var tf = FONTS[L.title.font], sf = FONTS[L.sub.font];
    r.tl.forEach(function (s) {
      drawLine(ctx, g, tf, s, r.ts, L.title.track || 0, 46, y + r.ts * r.tm.a * 0.92, L.titleColor);
      y += r.tlh;
    });
    if (r.tl.length && r.sl.length) {
      y += r.L.t.gap * r.k / (r.rule ? 2 : 1);
      if (r.rule) {
        ctx.fillStyle = L.accent;
        rect(ctx, g, 46 - 7 * r.k, y + r.rule / 2 - 0.18, 46 + 7 * r.k, y + r.rule / 2 + 0.18);
        y += r.rule + r.L.t.gap * r.k / 2;
      }
    }
    r.sl.forEach(function (s) {
      drawLine(ctx, g, sf, s, r.ss, L.sub.track || 0, 46, y + r.ss * r.sm.a * 0.98, L.subColor);
      y += r.slh;
    });
    if (r.frame > 0 && (r.tl.length || r.sl.length)) {
      ctx.strokeStyle = L.accent;
      // Two rules, the outer on the safe margin's line, the inner a millimetre in; padded by r.frame.
      var fy0 = r.top + r.inkTop - r.frame + 0.6, fy1 = r.top + r.inkTop + r.h + r.frame - 0.6;
      [[0, 0.4], [1, 0.22]].forEach(function (p) {
        ctx.lineWidth = p[1] * g.sy;
        var x = SAFE + 0.6 + p[0];
        ctx.strokeRect(g.ox + x * g.sx, g.oy + (fy0 + p[0]) * g.sy, (92 - 2 * x) * g.sx, (fy1 - fy0 - 2 * p[0]) * g.sy);
      });
    }
    return r;
  }

  /* Does this spec fit its box without going under the legibility floor? For the editor, to refuse
     a keystroke that would not (the note's rule). Measured on a throwaway context. */
  var probe = null;
  function fits(spec, opts) {
    probe = probe || document.createElement('canvas').getContext('2d');
    var r = layout(probe, spec, (opts && opts.box) || BLANK_BOX);
    return !r.small && !r.overflow;
  }

  /* ⭐ WHERE THE WORDS SIT, AND WHETHER THEY HAVE SHRUNK AS FAR AS THEY GO (2026-10-09, the builder's direction B).
     For the editor: the title's and the subtitle's bands in trim mm (y down from the top edge, as drawn), their size,
     and `atFloor`, true once the fitter has taken the words down to the legibility floor. The refusal is then every
     keystroke that lengthens the words, spaces included (B plan, "fixed in all three" 6: no more "happy returs",
     where a narrow letter or a space still went in after a wider one was refused). The bisection never lands exactly
     on the floor (memory: bisection never tests the floor), so "at" means within half a percent of it. */
  function measure(spec, opts) {
    probe = probe || document.createElement('canvas').getContext('2d');
    var box = (opts && opts.box) || BLANK_BOX, r = layout(probe, spec, box);
    var y = r.top + (r.dots && r.tl.length ? r.dots * r.k : 0), title = null, sub = null;
    if (r.tl.length) { title = { y0: y, y1: y + r.tl.length * r.tlh, size: r.ts, lines: r.tl.length }; y = title.y1; }
    if (r.tl.length && r.sl.length) { y += r.L.t.gap * r.k / (r.rule ? 2 : 1); if (r.rule) y += r.rule + r.L.t.gap * r.k / 2; }
    if (r.sl.length) sub = { y0: y, y1: y + r.sl.length * r.slh, size: r.ss, lines: r.sl.length };
    var floor = r.kMin || 0;
    return { k: r.k, floor: floor, atFloor: !!(r.small || r.overflow || (r.k - floor) <= 0.005 * Math.max(floor, 0.01) + 1e-9),
             small: !!r.small, overflow: !!r.overflow, title: title, sub: sub, box: box,
             titleFont: r.L.title.font, subFont: r.L.sub.font, titleUpper: !!r.L.title.upper, subUpper: !!r.L.sub.upper,
             titleTrack: r.L.title.track || 0, subTrack: r.L.sub.track || 0, titleColor: r.L.titleColor };
  }

  /* A photo on the face: cover-fit to the whole face (bleed included on the master), then the customer's zoom and
     position (-1..1 on each axis, the share of the overflow).
     ⭐ ZOOM BELOW 1 (24 Oct; Achinoam via Yonatan, Daniel's go: "a small picture next to an existing design"). Zoom ran
     1-3, always edge to edge; it now runs from ZOOM_MIN. On an axis where the photo no longer covers the face it is
     whole on that axis and the position slides it across the free space instead of along the overflow:
       x  across the whole face, bleed included (-3..95 mm): pushed to a side it bleeds off that edge like any art does,
          never stopping on the trim line, where the cut would leave a hairline of paper or nick the photo (Yonatan's
          verify_batch "artwork bleeds to the edge" caught exactly that on the first version, which stopped at the trim);
       y  in the space ABOVE THE GLUE BAND, from the head's bleed to 80 mm (the part of the inside top the pop-up never
          hides); taller than that it stands at the head and its foot goes under the strip, as a full photo's does.
     Zoom 1 and above is exactly the old rule, so every draft and order before it draws the same pixels.
     Returns the source rectangle (px) and where it lands (trim mm, y down from the top edge). */
  var ZOOM_MIN = 0.25, ZOOM_MAX = 3;
  function photoPlace(img, zoom, px, py) {
    var fw = 98, fh = 123;                             // the face, mm, with its bleed
    var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    var z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number(zoom) || 1));
    var s = Math.max(fw / iw, fh / ih) * z;            // mm per source px
    var dw = iw * s, dh = ih * s;                      // the whole photo at this zoom, mm
    var out = { srcW: iw, srcH: ih, zoom: z };
    // x
    if (dw >= fw - 1e-6) {
      var cw = fw / s, cx = (iw - cw) / 2 * (1 + (px || 0));
      out.x = Math.max(0, Math.min(iw - cw, cx)); out.w = cw; out.dx = -3; out.dw = fw;
    } else {
      out.x = 0; out.w = iw; out.dx = -3 + (fw - dw) * (1 + (px || 0)) / 2; out.dw = dw;
    }
    // y
    if (dh >= fh - 1e-6) {
      var ch = fh / s, cy = (ih - ch) / 2 * (1 + (py || 0));
      out.y = Math.max(0, Math.min(ih - ch, cy)); out.h = ch; out.dy = -3; out.dh = fh;
    } else {
      out.y = 0; out.h = ih; out.dy = -3 + Math.max(0, 83 - dh) * (1 + (py || 0)) / 2; out.dh = dh;
    }
    out.covers = out.dw >= fw - 1e-6 && out.dh >= fh - 1e-6;
    return out;
  }
  // The crop in source px, as `_Src 5` reports it (kept for configurator.js), and its printed width in mm.
  function photoCrop(img, zoom, px, py) {
    var c = photoPlace(img, zoom, px, py);
    return { x: c.x, y: c.y, w: c.w, h: c.h, srcW: c.srcW, srcH: c.srcH, mmW: Math.min(c.dw, 98) };
  }
  /* What the photo layer needs that a JPEG cannot carry: the face not covered, a see-through photo, or a cut-out.
     Then it is baked as a transparent PNG and print shows the design's text-free top through it (print/photos.mjs
     attachTopLayer already keeps a PNG's alpha and composites it over top-plain, 8 Oct). */
  function photoNeedsAlpha(img, p) {
    if (!p) return false;
    var op = p.opacity == null ? 1 : Number(p.opacity);
    return (isFinite(op) && op < 0.999) || !!p.cut || !photoPlace(img, p.zoom, p.px, p.py).covers;
  }
  // `cut` (24 Oct): the person alone, the photo's pixels where PPVision's mask is (configurator.js hands the mask in).
  function drawPhoto(ctx, g, img, p, mask) {
    var c = photoPlace(img, p.zoom, p.px, p.py);
    var src = img;
    if (mask) {
      var m = document.createElement('canvas');
      m.width = img.naturalWidth || img.width; m.height = img.naturalHeight || img.height;
      var mx = m.getContext('2d');
      mx.drawImage(img, 0, 0, m.width, m.height);
      mx.globalCompositeOperation = 'destination-in';
      mx.drawImage(mask, 0, 0, m.width, m.height);
      src = m;
    }
    var op = p.opacity == null ? 1 : Math.min(1, Math.max(0, Number(p.opacity)));
    ctx.save();
    ctx.imageSmoothingQuality = 'high';
    ctx.globalAlpha = isFinite(op) ? op : 1;
    ctx.drawImage(src, c.x, c.y, c.w, c.h, g.ox + c.dx * g.sx, g.oy + c.dy * g.sy, c.dw * g.sx, c.dh * g.sy);
    ctx.restore();
    if (src !== img) { src.width = src.height = 0; }
    return c;
  }

  function clearBand(ctx, g) {
    ctx.clearRect(g.ox + BAND.x0 * g.sx, g.oy + BAND.y0 * g.sy, (BAND.x1 - BAND.x0) * g.sx, g.h);
  }

  /* The preview: white, the design's text-free top, then the layer, glue band bare (white, never clear: see below). `base` is a decoded
     image of CoverTopPlain (the web face, 1000 x 1332 of the trim). Returns { canvas, overflow }. */
  function preview(base, spec, opts, w) {
    var g = previewGeom(w || 1000);
    var c = document.createElement('canvas'); c.width = g.w; c.height = g.h;
    var ctx = c.getContext('2d');
    /* ⛔ WHITE FIRST, ALWAYS (13 Oct). The text-free top (top-plain.webp) leaves its glue band CLEAR, and this canvas is
       encoded as JPEG (configurator.js _topRenderNow, the template tiles), which has no alpha: a clear pixel comes out
       BLACK. At rest the open strip covers the band, so nobody saw it; while the card opens it is bare, and Daniel saw a
       black box across the middle of the card for the length of the opening. White is what the plane shows under a
       clear pixel anyway (memory: "Canvas->JPEG makes transparency black"). */
    ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, 0, g.w, g.h);
    if (base) ctx.drawImage(base, 0, 0, g.w, g.h);
    var layer = document.createElement('canvas'); layer.width = g.w; layer.height = g.h;
    var lc = layer.getContext('2d'), r = null;
    if (spec.mode === 'photo' && opts && opts.photo) drawPhoto(lc, g, opts.photo, spec.photo || {}, opts.mask);
    else if (spec.mode === 'text') r = drawWords(lc, g, spec, opts);
    clearBand(lc, g);
    ctx.drawImage(layer, 0, 0);
    return { canvas: c, overflow: !!(r && r.overflow), small: !!(r && r.small) };
  }

  /* The bake, on the print grid. Text: a transparent PNG, band cleared. Photo: a JPEG on white (print
     clears the band; a transparent pixel would encode black), or since 24 Oct a transparent PNG, band cleared, when
     the photo is smaller than the face, see-through or cut out (photoNeedsAlpha). Resolves { blob, src, alpha } where
     `src` is the photo's true crop for `_Src 5` ("WxH of WxH"), or ''. */
  function masterAt(w) {
    var h = Math.round(w * 2906 / 2315);
    return { w: w, h: h, sx: w / 98, sy: h / 123, ox: 3 * w / 98, oy: 3 * h / 123, full: MASTER.full };
  }
  function bake(spec, opts) {
    var alpha = spec.mode === 'photo' && (photoNeedsAlpha(opts.photo, spec.photo || {}) || !!opts.mask);
    /* ⚠️ CLOUDINARY'S FREE PLAN REFUSES ANY IMAGE OVER 10 MB (10,485,760 bytes, measured 2026-10-11),
       and this is the largest thing the builder uploads: 2315 x 2906. Firefox encodes q0.92 without
       chroma subsampling, so a very detailed photo bakes far bigger there: a photo-sized field of
       noise made 10.66 MB in Firefox (4.66 Chromium, 6.28 WebKit), and the order stopped at "We
       couldn't prepare the inside of your card". A real grainy photo made 2.67 MB, so this is rare.
       Only over the line, step the QUALITY down (q0.88 made that same noise 4.45 MB); the pixel
       size never changes, so print keeps its 600 dpi grid.
       ⚠️ A PHOTO AS A PNG (24 Oct) has no quality to step: a full-face see-through photo is ~3 bytes a pixel, near
       20 MB at 2315. So the PNG steps the GRID down instead, 600 → 450 → 300 dpi, keeping 98:123 inside print's
       0.5 % (print places the layer by millimetres and judges sharpness from `_Src 5`, never from the file). A small
       photo on a clear face compresses far below the line and keeps 600. */
    var LIMIT = 9.5 * 1000 * 1000;
    var steps = spec.mode !== 'photo' ? [[2315, 0.92]]
      : alpha ? [[2315, 1], [1736, 1], [1157, 1]]
      : [[2315, 0.92], [2315, 0.88], [2315, 0.8], [2315, 0.7]];
    var type = (spec.mode === 'photo' && !alpha) ? 'image/jpeg' : 'image/png';
    var src = '';
    var draw = function (w) {
      var g = w === 2315 ? MASTER : masterAt(w);
      var c = document.createElement('canvas'); c.width = g.w; c.height = g.h;
      var ctx = c.getContext('2d');
      if (spec.mode === 'photo') {
        if (!alpha) { ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, 0, g.w, g.h); }
        var cp = drawPhoto(ctx, g, opts.photo, spec.photo || {}, opts.mask);
        src = Math.round(cp.w) + 'x' + Math.round(cp.h) + ' of ' + cp.srcW + 'x' + cp.srcH;
        if (alpha) clearBand(ctx, g);
      } else {
        drawWords(ctx, g, spec, opts);
        clearBand(ctx, g);
      }
      return c;
    };
    return new Promise(function (res, rej) {
      var c = null, at = -1;
      (function encode(i) {
        if (steps[i][0] !== at) { if (c) c.width = c.height = 0; at = steps[i][0]; c = draw(at); }
        c.toBlob(function (b) {
          if (b && b.size > LIMIT && i + 1 < steps.length) return encode(i + 1);
          c.width = c.height = 0;                     // give the 27 MB back at once on phones
          if (b) res({ blob: b, src: src, alpha: alpha }); else rej(new Error('inside top: the canvas made no image'));
        }, type, steps[i][1]);
      })(0);
    });
  }

  // Load the faces a spec uses (or every face, for the template picker) before drawing.
  function fontsFor(spec) {
    if (!spec) return Object.keys(FONTS).map(function (k) { return FONTS[k]; });
    var L = look(spec); return [FONTS[L.title.font], FONTS[L.sub.font]];
  }
  function ensureFonts(spec) {
    if (!document.fonts || !document.fonts.load) return Promise.resolve();
    return Promise.all(fontsFor(spec).map(function (f) {
      return document.fonts.load(fontCss(f, 40), 'Happy birthday').catch(function () {});
    }));
  }

  window.PPTop = {
    FONTS: FONTS, TITLE_FONTS: TITLE_FONTS, COLOURS: COLOURS, TEMPLATES: TEMPLATES, OFFERED: OFFERED, coverAccent: coverAccent, accentOfPixels: accentOfPixels, BLANK_BOX: BLANK_BOX,
    START: START, NONE: NONE, words: words, optsFor: optsFor, startSpec: startSpec,
    look: look, preview: preview, bake: bake, ensureFonts: ensureFonts, photoCrop: photoCrop, hexOf: hexOf,
    photoPlace: photoPlace, photoNeedsAlpha: photoNeedsAlpha, ZOOM_MIN: ZOOM_MIN, ZOOM_MAX: ZOOM_MAX,
    oneLine: oneLine, fits: fits, measure: measure, SAFE: SAFE, BAND: BAND
  };
})();
